import { useCallback } from "react"
import { useMutation } from "urql"

import {
  type GoodsReceiptPallet,
  type GoodsReceiptPalletUpdateFailure,
  type GoodsReceiptPalletUpdateSuccess,
  UpdateGoodsReceiptPalletDocument,
  type UpdateGoodsReceiptPalletMutation,
  type UpdateGoodsReceiptPalletMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseUpdateGoodsReceiptPalletProps {
  onSuccess?: () => void
}

interface UseUpdateGoodsReceiptPalletReturn {
  updatePallet: (params: UpdateGoodsReceiptPalletMutationVariables) => Promise<ActionOutcome<GoodsReceiptPallet>>
  isUpdatingPallet: boolean
}

export const useUpdateGoodsReceiptPallet = ({
  onSuccess,
}: UseUpdateGoodsReceiptPalletProps = {}): UseUpdateGoodsReceiptPalletReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    UpdateGoodsReceiptPalletMutation,
    UpdateGoodsReceiptPalletMutationVariables
  >(UpdateGoodsReceiptPalletDocument)

  const updatePallet = useCallback(
    async ({
      goodsReceiptPalletId,
      destinationWarehouseShelvingId,
      lpn,
    }: UpdateGoodsReceiptPalletMutationVariables): Promise<ActionOutcome<GoodsReceiptPallet>> => {
      const { data, error } = await executeMutation({
        goodsReceiptPalletId,
        destinationWarehouseShelvingId,
        lpn,
      })
      const output = data?.goodsReceipt.overridePallet
      const hasError = error || output?.__typename === "GoodsReceiptPalletUpdateFailure"

      if (hasError) {
        const errorMessage =
          error?.message ?? (output as GoodsReceiptPalletUpdateFailure).error?.message ?? "Failed to update pallet."
        notify({
          type: NotificationType.Error,
          message: errorMessage,
        })
        return {
          error: new Error(errorMessage),
        }
      }

      notify({
        type: NotificationType.Success,
        content: "Pallet updated successfully.",
      })
      onSuccess?.()

      return {
        data: (output as GoodsReceiptPalletUpdateSuccess).goodsReceiptPallet,
      }
    },
    [executeMutation, notify, onSuccess]
  )

  return { updatePallet, isUpdatingPallet: fetching }
}
